import React from 'react';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { getIcon } from '@flybits/design-system';
import { EVENT_KEYS } from 'types/events';
import { getDeliveryFormOptions } from 'store/push/push.selector';
import { selectStepRule } from 'store/rule/rule.selector';
import { getStepRuleSyntheticId } from 'store/helper';
import AdvancedForm from './AdvancedForm';
import { PushDraft, PushDraftCallbacks, RuleDraft } from '../pushDraft.types';

type PushFormAdvancedProps = Pick<PushDraftCallbacks, 'onChangePushField' | 'onChangeRuleField'> & {
  classPrefix: string;
  onToggle: () => void;
  stepIdx: number;
  isExpanded: boolean;
  pushDraft: PushDraft;
  ruleDraft: RuleDraft;
  isJourneyLocked: boolean;
};

export default function PushFormAdvanced({
  classPrefix = '',
  onToggle,
  stepIdx,
  isExpanded = false,
  pushDraft,
  ruleDraft,
  onChangePushField,
  onChangeRuleField,
}: PushFormAdvancedProps) {
  const ruleRepeatSchedule = useSelector((state) => {
    const frequency = state.te.rule.startSchedulerMap[getStepRuleSyntheticId(stepIdx)]?.frequency;
    return frequency && frequency > 0;
  });

  const [deliveryValues, ruleRefId] = useSelector((state) => {
    const pushRefId = state.te.journey.steps[stepIdx]?.push ?? pushDraft.refId ?? '';
    const rule = selectStepRule(state.te, stepIdx);
    return [getDeliveryFormOptions(state.te, pushRefId, stepIdx), rule?.refId ?? ''];
  });

  const shouldRender = pushDraft.actionType !== 'broadcast-push' && !ruleRepeatSchedule;

  return (
    <>
      {shouldRender && (
        <div className={`${classPrefix}__advanced`}>
          <div
            className={`${classPrefix}__advanced-header`}
            onClick={onToggle}
            onKeyDown={(e) => {
              if (e.key === EVENT_KEYS.ENTER) {
                onToggle();
              }
            }}
            aria-label={'toggle advanced push settings'}
            role="button"
            tabIndex={0}
          >
            Advanced Settings
            {getIcon(isExpanded ? 'baselineKeyboardArrowUp' : 'baselineKeyboardArrowDown', {})}
          </div>
          {isExpanded ? (
            <AdvancedForm
              classPrefix={classPrefix}
              pushDraft={pushDraft}
              ruleDraft={ruleDraft}
              deliveryValues={deliveryValues}
              constraints={pushDraft.constraints}
              ruleRefId={ruleRefId || ruleDraft.refId}
              onChangePushField={onChangePushField}
              onChangeRuleField={onChangeRuleField}
            />
          ) : null}
        </div>
      )}
    </>
  );
}
